import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertTriangle, Plus, Trash2 } from "lucide-react";
import { useFieldArray, useForm } from "react-hook-form";
import {
  createCrewSchema,
  type CreateCrewInput,
  type CrewFormOptions,
} from "@crewledger/shared/schemas";
import { CommandCenterCard, PageHeader } from "@/components/design";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { createCrew, fetchCrew, fetchCrewOptions, updateCrew } from "./api";
import { useCrewPermissions } from "./use-crew-permissions";

interface CrewFormPageProps {
  mode: "create" | "edit";
}

export function CrewFormPage({ mode }: CrewFormPageProps) {
  const { crewId } = useParams();
  const navigate = useNavigate();
  const { canWrite } = useCrewPermissions();
  const [options, setOptions] = useState<CrewFormOptions | null>(null);
  const [loading, setLoading] = useState(mode === "edit");
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<CreateCrewInput>({
    resolver: zodResolver(createCrewSchema),
    defaultValues: {
      name: "",
      description: "",
      siteId: null,
      active: true,
      members: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "members",
  });

  useEffect(() => {
    fetchCrewOptions()
      .then((res) => { setOptions(res.data); })
      .catch(() => { setOptions(null); });
  }, []);

  useEffect(() => {
    if (mode !== "edit" || !crewId) return;
    setLoading(true);
    fetchCrew(crewId)
      .then((res) => {
        const crew = res.data;
        form.reset({
          name: crew.name,
          description: crew.description ?? "",
          siteId: crew.siteId,
          active: crew.active,
          members: crew.members.map((m) => ({
            workerId: m.workerId,
            defaultJobCode: m.defaultJobCode ?? "",
            defaultCostCode: m.defaultCostCode ?? "",
            defaultEquipmentId: m.defaultEquipmentId,
            role: m.role ?? "",
          })),
        });
      })
      .catch((err: unknown) =>
        { setSubmitError(err instanceof Error ? err.message : "Failed to load crew"); },
      )
      .finally(() => { setLoading(false); });
  }, [mode, crewId, form]);

  if (!canWrite) {
    return <PageHeader title="Access denied" description="You do not have permission to edit crews." />;
  }

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmitError(null);
    const payload: CreateCrewInput = {
      ...values,
      description: values.description?.trim() ? values.description.trim() : null,
      siteId: values.siteId || null,
      members: values.members.map((m) => ({
        workerId: m.workerId,
        defaultJobCode: m.defaultJobCode?.trim() || null,
        defaultCostCode: m.defaultCostCode?.trim() || null,
        defaultEquipmentId: m.defaultEquipmentId || null,
        role: m.role?.trim() || null,
      })),
    };
    try {
      if (mode === "create") {
        const res = await createCrew(payload);
        navigate(`/crews/${res.data.id}`);
      } else if (crewId) {
        const res = await updateCrew(crewId, payload);
        navigate(`/crews/${res.data.id}`);
      }
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to save crew");
    }
  });

  const addMember = () => {
    const usedIds = new Set(form.getValues("members").map((m) => m.workerId));
    const available = options?.workers.find((w) => !usedIds.has(w.id));
    if (!available) return;
    append({
      workerId: available.id,
      defaultJobCode: "",
      defaultCostCode: "",
      defaultEquipmentId: null,
      role: "",
    });
  };

  const siteEquipment = (siteId: string | null | undefined) =>
    options?.equipment.filter((e) => !siteId || e.siteId === siteId || e.siteId === null) ?? [];

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <PageHeader
        title={mode === "create" ? "New crew template" : "Edit crew template"}
        description="Define workers and defaults used when adding a crew to daily entry."
      />

      {loading ? (
        <div className="h-96 animate-pulse rounded-xl border bg-card-white" />
      ) : (
        <form onSubmit={(e) => void onSubmit(e)} className="space-y-6">
          <div className="rounded-xl border bg-card-white p-6 space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="name">Crew name</Label>
                <Input id="name" {...form.register("name")} placeholder="Harbor Pile Crew" />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="description">Description</Label>
                <textarea
                  id="description"
                  {...form.register("description")}
                  rows={2}
                  className="w-full rounded-md border border-input px-3 py-2 text-sm"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="siteId">Site</Label>
                <select
                  id="siteId"
                  value={form.watch("siteId") ?? ""}
                  onChange={(e) => { form.setValue("siteId", e.target.value || null); }}
                  className="flex h-9 w-full rounded-md border border-input px-3 text-sm"
                >
                  <option value="">All sites</option>
                  {options?.sites.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.code} — {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2 pt-6">
                <input type="checkbox" id="active" {...form.register("active")} />
                <Label htmlFor="active">Active</Label>
              </div>
            </div>
          </div>

          <CommandCenterCard
            title="Crew members"
            description="Set default job code, cost code, and equipment for daily entry."
            action={
              <Button type="button" variant="outline" size="sm" onClick={addMember}>
                <Plus className="mr-1.5 h-4 w-4" />
                Add worker
              </Button>
            }
          >
            {fields.length === 0 ? (
              <p className="text-sm text-muted-foreground">No workers added yet.</p>
            ) : (
              <div className="space-y-3">
                {fields.map((field, index) => {
                  const workerId = form.watch(`members.${index}.workerId`);
                  const worker = options?.workers.find((w) => w.id === workerId);
                  const equip = siteEquipment(form.watch("siteId"));
                  return (
                    <div key={field.id} className="rounded-lg border p-3">
                      <div className="mb-3 flex items-start justify-between gap-2">
                        <div>
                          <select
                            value={workerId}
                            onChange={(e) => { form.setValue(`members.${index}.workerId`, e.target.value); }}
                            className="flex h-9 w-full rounded-md border border-input px-3 text-sm font-medium"
                          >
                            {options?.workers.map((w) => (
                              <option key={w.id} value={w.id}>
                                {w.employeeId} — {w.displayName}
                                {w.status === "INACTIVE" ? " (inactive)" : ""}
                              </option>
                            ))}
                          </select>
                          {worker?.status === "INACTIVE" ? (
                            <p className="mt-1 flex items-center gap-1 text-xs text-amber-700">
                              <AlertTriangle className="h-3 w-3" />
                              Inactive worker — daily entry will warn
                            </p>
                          ) : null}
                        </div>
                        <Button type="button" variant="ghost" size="sm" onClick={() => { remove(index); }}>
                          <Trash2 className="h-4 w-4 text-red-danger" />
                        </Button>
                      </div>
                      <div className="grid gap-2 sm:grid-cols-3">
                        <Input
                          placeholder="Default job code"
                          {...form.register(`members.${index}.defaultJobCode`)}
                        />
                        <Input
                          placeholder="Default cost code"
                          {...form.register(`members.${index}.defaultCostCode`)}
                        />
                        <select
                          value={form.watch(`members.${index}.defaultEquipmentId`) ?? ""}
                          onChange={(e) =>
                            { form.setValue(
                              `members.${index}.defaultEquipmentId`,
                              e.target.value || null,
                            ); }
                          }
                          className="flex h-9 rounded-md border border-input px-2 text-sm"
                        >
                          <option value="">No default equipment</option>
                          {equip.map((e) => (
                            <option key={e.id} value={e.id}>
                              {e.equipmentId} — {e.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CommandCenterCard>

          {submitError ? <p className="text-sm text-red-danger">{submitError}</p> : null}

          <div className="flex gap-2">
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? "Saving…" : mode === "create" ? "Create crew" : "Save changes"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(mode === "edit" && crewId ? `/crews/${crewId}` : "/crews")}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

export function CrewCreatePage() {
  return <CrewFormPage mode="create" />;
}

export function CrewEditPage() {
  return <CrewFormPage mode="edit" />;
}
